import { unstable_useId as useId } from 'reakit';
import type { ComponentPropsWithoutRef, MouseEvent } from 'react';
import { forwardRef, useContext, useMemo } from 'react';
import { useRegisterRow } from '../internal/useRegisterRow';
import { Box } from '../../Box/Box';
import { Text } from '../../Text/Text';
import { Row } from '../internal/Row';
import type { OptionParams } from '../internal/types';
import { searchContext } from '../internal/context';
import { createLanguageGetter } from '../internal/text';
import pxToRem from '../../utils/pxToRem';

type ToggleHistoryOptionProps = Omit<
  ComponentPropsWithoutRef<'div'>,
  'role' | 'children'
>;

export const ToggleHistoryOption = forwardRef<
  HTMLDivElement,
  ToggleHistoryOptionProps
>(function ToggleHistoryOption(props, ref) {
  let { id } = useId(props) as { id: string };
  const cellIds = useMemo<OptionParams['cellIds']>(
    function getCellIds() {
      return [`${id}-content`];
    },
    [id]
  );
  const { send } = useContext(searchContext);
  useRegisterRow({
    type: 'history_toggle',
    id,
    cellIds,
    value: '',
  });
  const { lang, historyEnabled } = useContext(searchContext);
  const getText = useMemo(() => createLanguageGetter(lang), [lang]);
  return (
    <Row
      ref={ref}
      {...props}
      id={id}
      pl={['sp-12', pxToRem(46)]}
      onMouseUp={(event: MouseEvent<HTMLDivElement>) => {
        props.onMouseUp?.(event);
        if (event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
        send({ type: 'toggle_history' });
      }}
    >
      <Box role="gridcell">
        <Text
          textAlign="left"
          color="gray-65"
          as="button"
          type="button"
          onClick={(event: Event) => event.preventDefault()}
          id={cellIds[0]}
        >
          {historyEnabled
            ? getText('disableHistory')
            : getText('enableHistory')}
        </Text>
      </Box>
    </Row>
  );
});

ToggleHistoryOption.displayName = 'ToggleHistoryOption';
